import { useCallback, useEffect, useRef, useState } from 'react';
import { apiService } from '../services/api';
import { getRealtimeErrorMessage } from '../features/realtime/errors';

/** Authenticated realtime media with state-change recovery and owned blob URLs. */
export const useRealtimeMedia = (path: string | null, availability?: string) => {
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState({ path, url: '', error: '', loading: Boolean(path) });
  const resolved = useRef({ path: null as string | null, url: '', revision: 0 });
  const reload = useCallback(() => setRevision(current => current + 1), []);

  useEffect(() => {
    let disposed = false;
    if (resolved.current.path === path && resolved.current.url && resolved.current.revision === revision) return;
    if (resolved.current.path !== path && resolved.current.url) {
      URL.revokeObjectURL(resolved.current.url);
      resolved.current = { path: null, url: '', revision };
    }
    setState({ path, url: '', error: '', loading: Boolean(path) });
    if (path) void apiService.getRealtimeClip(path).then(blob => {
      if (disposed) return;
      const url = URL.createObjectURL(blob);
      if (resolved.current.url) URL.revokeObjectURL(resolved.current.url);
      resolved.current = { path, url, revision };
      setState({ path, url, error: '', loading: false });
    }).catch(error => {
      if (!disposed) setState({ path, url: '', error: getRealtimeErrorMessage(error, 'Unable to load media.'), loading: false });
    });
    return () => { disposed = true; };
  }, [path, availability, revision]);

  useEffect(() => () => {
    if (resolved.current.url) URL.revokeObjectURL(resolved.current.url);
  }, []);

  return { ...(state.path === path ? state : { url: '', error: '', loading: Boolean(path) }), reload };
};
